import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  savedContentTypes,
  savedItems,
  savedDashboardStats,
  savedUserProfile,
  saveScoreActions,
  getSavedItemsByFilter,
  getSavedItemsByType,
  getContentTypeById,
  type SavedFilterId,
  type SavedContentTypeId,
  type SavedItem,
} from '@/mocks/rfwSavedItems';

function SavedListCard({ item }: { item: SavedItem }) {
  const typeInfo = getContentTypeById(item.type);
  return (
    <div className="flex items-start gap-4 p-4 rounded-2xl bg-background-50 border border-background-200/70 hover:border-foreground-300/50 transition-all duration-200 group">
      <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0 relative">
        <img src={item.thumbnailUrl} alt={item.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
        {typeInfo && (
          <div className="absolute top-2 left-2">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-semibold bg-white/20 backdrop-blur-sm text-white`}>
              <i className={`${typeInfo.icon} text-[7px]`}></i>
              {typeInfo.name}
            </span>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          {item.isFavorite && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-amber-100 text-amber-600">
              <i className="ri-star-fill text-[7px]"></i>FAV
            </span>
          )}
          {item.isPending && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] font-semibold bg-violet-100 text-violet-600">
              <i className="ri-time-line text-[7px]"></i>PENDIENTE
            </span>
          )}
        </div>
        <h4 className="text-sm font-bold text-foreground-950 line-clamp-2 leading-snug mb-1">{item.title}</h4>
        {item.subtitle && (
          <p className="text-[10px] text-foreground-500 mb-2">{item.subtitle}</p>
        )}
        <div className="flex flex-wrap items-center gap-2 text-[10px] text-foreground-500">
          <div className="flex items-center gap-1.5">
            <img src={item.authorPhoto} alt={item.authorName} className="w-5 h-5 rounded-full object-cover object-top" />
            <span className="font-medium">{item.radioName}</span>
          </div>
          <span>·</span>
          <span><i className="ri-eye-line mr-0.5 text-[9px]"></i>{item.viewCount.toLocaleString()}</span>
          <span>·</span>
          <span className="whitespace-nowrap">{item.savedAt}</span>
        </div>
        {item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {item.tags.map((tag) => (
              <span key={tag} className="px-1.5 py-0.5 rounded-full bg-background-100 text-[9px] text-foreground-500 font-medium">
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="shrink-0 flex flex-col items-end gap-2">
        <button className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center cursor-pointer hover:bg-teal-100 transition-colors">
          <i className="ri-bookmark-fill text-teal-600 text-sm"></i>
        </button>
        {item.isPending && (
          <button className="w-8 h-8 rounded-full bg-white border border-background-200/70 flex items-center justify-center cursor-pointer hover:bg-background-100 transition-colors">
            <i className="ri-check-line text-foreground-400 text-sm"></i>
          </button>
        )}
      </div>
    </div>
  );
}

function SavedGridCard({ item }: { item: SavedItem }) {
  const typeInfo = getContentTypeById(item.type);
  return (
    <div className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-foreground-300/50 transition-all duration-200 group flex flex-col">
      <div className="relative h-40 overflow-hidden">
        <img src={item.thumbnailUrl} alt={item.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute top-3 left-3">
          {typeInfo && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-white/20 backdrop-blur-sm text-white">
              <i className={`${typeInfo.icon} text-[9px]`}></i>
              {typeInfo.name}
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3 flex flex-col gap-1.5">
          {item.isFavorite && (
            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-500 text-white">
              <i className="ri-star-fill text-[8px]"></i>
            </span>
          )}
          {item.isPending && (
            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-violet-500/80 text-white">
              <i className="ri-time-line text-[8px]"></i>Pend
            </span>
          )}
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h4 className="text-sm font-bold text-foreground-950 line-clamp-2 leading-snug mb-2">{item.title}</h4>
        {item.subtitle && (
          <p className="text-[10px] text-foreground-500 mb-2">{item.subtitle}</p>
        )}
        <div className="flex items-center gap-2 mt-auto">
          <img src={item.authorPhoto} alt={item.authorName} className="w-5 h-5 rounded-full object-cover object-top" />
          <span className="text-[10px] text-foreground-500 font-medium truncate">{item.radioName}</span>
          <span className="text-[9px] text-foreground-400 ml-auto whitespace-nowrap">{item.savedAt}</span>
        </div>
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-background-200/70">
          <span className="text-[10px] text-foreground-500"><i className="ri-eye-line mr-0.5"></i>{item.viewCount.toLocaleString()}</span>
          <button className="w-7 h-7 rounded-full bg-teal-50 flex items-center justify-center cursor-pointer hover:bg-teal-100 transition-colors">
            <i className="ri-bookmark-fill text-teal-600 text-xs"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GuardadosPage() {
  const [view, setView] = useState<'todos' | 'favoritos' | 'pendientes'>('todos');
  const [selectedType, setSelectedType] = useState<SavedContentTypeId | 'todas'>('todas');
  const [selectedFilter, setSelectedFilter] = useState<SavedFilterId>('recientes');
  const [layout, setLayout] = useState<'list' | 'grid'>('list');

  const baseItems = selectedType === 'todas' ? savedItems : getSavedItemsByType(selectedType);
  const filteredByView = view === 'favoritos'
    ? baseItems.filter((item) => item.isFavorite)
    : view === 'pendientes'
    ? baseItems.filter((item) => item.isPending)
    : baseItems;
  const sortedItems = getSavedItemsByFilter(selectedFilter).filter((item) => filteredByView.some((f) => f.id === item.id));

  const views = [
    { id: 'todos' as const, label: 'Todos', icon: 'ri-bookmark-line' },
    { id: 'favoritos' as const, label: 'Favoritos', icon: 'ri-star-line' },
    { id: 'pendientes' as const, label: 'Pendientes', icon: 'ri-time-line' },
  ];

  const filters: { id: SavedFilterId; label: string; icon: string }[] = [
    { id: 'recientes', label: 'Recientes', icon: 'ri-time-line' },
    { id: 'mas-vistos', label: 'Más Vistos', icon: 'ri-eye-line' },
    { id: 'favoritos', label: 'Favoritos', icon: 'ri-star-line' },
    { id: 'pendientes', label: 'Pendientes', icon: 'ri-hourglass-line' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-teal-500 via-emerald-500 to-cyan-600">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-semibold mb-5">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
            SISTEMA DE GUARDADOS RFW
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Todo Lo Que Importa, <span className="text-teal-200">Siempre a Mano</span>
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto text-sm md:text-base mb-8">
            Guardá posts, videos, eventos, concursos, programas, locutores y radios con un solo toque. Accedé rápido desde tu perfil con filtros inteligentes y nunca pierdas lo que te importa.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto">
            {[
              { label: 'Guardados', value: savedDashboardStats.totalSaved.toLocaleString(), icon: 'ri-bookmark-line' },
              { label: 'Hoy', value: savedDashboardStats.totalSavedToday.toLocaleString(), icon: 'ri-calendar-line' },
              { label: 'Favoritos', value: savedDashboardStats.totalFavorites.toLocaleString(), icon: 'ri-star-line' },
              { label: 'Pendientes', value: savedDashboardStats.totalPending.toLocaleString(), icon: 'ri-time-line' },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-2 p-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10">
                <i className={`${stat.icon} text-white/70 text-lg`}></i>
                <div className="text-left">
                  <p className="text-lg font-bold text-white">{stat.value}</p>
                  <p className="text-[10px] text-white/60">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* View Switcher + Profile Quick Access */}
      <div className="sticky top-0 z-30 bg-background-50/80 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <img src={savedUserProfile.photo} alt={savedUserProfile.name} className="w-8 h-8 rounded-full object-cover object-top ring-2 ring-teal-200" />
              <div className="hidden sm:block">
                <p className="text-xs font-semibold text-foreground-950">{savedUserProfile.name}</p>
                <p className="text-[9px] text-foreground-500">{savedUserProfile.totalSaved} guardados · {savedUserProfile.totalFavorites} favs</p>
              </div>
            </div>
            <div className="flex items-center gap-1 overflow-x-auto no-scrollbar flex-1">
              {views.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setView(v.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    view === v.id
                      ? 'bg-teal-500 text-white shadow-sm'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${v.icon} text-[10px]`}></i>
                  {v.label}
                </button>
              ))}
              <div className="w-px h-5 bg-background-200/70 mx-1"></div>
              <button
                onClick={() => setLayout(layout === 'list' ? 'grid' : 'list')}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-foreground-600 hover:text-foreground-900 whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className={`${layout === 'list' ? 'ri-grid-line' : 'ri-list-check'} text-[10px]`}></i>
                {layout === 'list' ? 'Grid' : 'Lista'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Type Filters */}
        <div className="flex flex-wrap items-center gap-1.5 mb-4">
          {[{ id: 'todas' as const, name: 'Todas', icon: 'ri-apps-line' }, ...savedContentTypes.map((ct) => ({ id: ct.id, name: ct.name, icon: ct.icon }))].map((type) => (
            <button
              key={type.id}
              onClick={() => setSelectedType(type.id)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                selectedType === type.id
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className={`${type.icon} text-[9px]`}></i>
              {type.name}
            </button>
          ))}
        </div>

        {/* Filter Bar */}
        <div className="flex items-center gap-1.5 mb-6">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setSelectedFilter(filter.id)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                selectedFilter === filter.id
                  ? 'bg-teal-100 text-teal-700 border border-teal-200/70'
                  : 'bg-background-100 text-foreground-500 hover:text-foreground-900'
              }`}
            >
              <i className={`${filter.icon} text-[9px]`}></i>
              {filter.label}
            </button>
          ))}
        </div>

        {/* Items */}
        {layout === 'list' ? (
          <div className="space-y-3">
            {sortedItems.map((item) => (
              <SavedListCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {sortedItems.map((item) => (
              <SavedGridCard key={item.id} item={item} />
            ))}
          </div>
        )}

        {sortedItems.length === 0 && (
          <div className="text-center py-14">
            <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-bookmark-line text-3xl text-teal-400"></i>
            </div>
            <p className="text-base font-semibold text-foreground-950 mb-1">No hay guardados aquí</p>
            <p className="text-sm text-foreground-500">Probá con otro filtro o empezá a guardar contenido que te guste.</p>
          </div>
        )}

        {/* Stats & Score Section */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stats */}
          <div className="lg:col-span-2 p-6 rounded-2xl bg-background-100 border border-background-200/70">
            <h3 className="text-base font-bold text-foreground-950 mb-4">Estadísticas de Guardados</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Guardados', value: savedDashboardStats.totalSaved.toLocaleString(), icon: 'ri-bookmark-line', color: 'text-teal-600 bg-teal-50' },
                { label: 'Total Vistas', value: (savedDashboardStats.totalViews / 1000000).toFixed(1) + 'M', icon: 'ri-eye-line', color: 'text-sky-600 bg-sky-50' },
                { label: 'Favoritos', value: savedDashboardStats.totalFavorites.toLocaleString(), icon: 'ri-star-line', color: 'text-amber-600 bg-amber-50' },
                { label: 'Promedio x Usuario', value: savedDashboardStats.avgSavedPerUser.toString(), icon: 'ri-user-line', color: 'text-emerald-600 bg-emerald-50' },
              ].map((stat, i) => (
                <div key={i} className="p-4 rounded-xl bg-background-50 border border-background-200/70">
                  <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center mb-2`}>
                    <i className={`${stat.icon} text-sm`}></i>
                  </div>
                  <p className="text-xl font-bold text-foreground-950">{stat.value}</p>
                  <p className="text-[10px] text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Type Distribution */}
            <h4 className="text-sm font-bold text-foreground-950 mt-6 mb-3">Distribución por Tipo de Contenido</h4>
            <div className="space-y-2">
              {savedContentTypes.map((ct) => (
                <div key={ct.id} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full ${ct.bgColor} flex items-center justify-center shrink-0`}>
                    <i className={`${ct.icon} ${ct.color} text-sm`}></i>
                  </div>
                  <span className="text-xs font-medium text-foreground-700 w-20">{ct.name}</span>
                  <div className="flex-1 h-2 rounded-full bg-background-200 overflow-hidden">
                    <div
                      className={`h-full rounded-full bg-teal-500`}
                      style={{ width: `${(ct.totalSaved / savedContentTypes[6].totalSaved) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-xs font-semibold text-foreground-950 w-16 text-right">{ct.totalSaved.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Score */}
          <div className="p-6 rounded-2xl bg-teal-50 border border-teal-200/50">
            <h3 className="text-base font-bold text-foreground-950 mb-4">Score por Guardar</h3>
            <div className="p-4 rounded-xl bg-white/70 border border-teal-100/50 mb-4">
              <p className="text-[9px] text-foreground-500 mb-1">Score Total Generado</p>
              <p className="text-2xl font-bold text-teal-600">{savedDashboardStats.totalScoreGenerated.toLocaleString()}</p>
              <p className="text-[9px] text-foreground-400 mt-0.5">puntos RFW</p>
            </div>
            <p className="text-[10px] font-semibold text-foreground-700 mb-3">Los guardados ayudan a medir interés real</p>
            <div className="space-y-2">
              {saveScoreActions.slice(0, 4).map((sa) => (
                <div key={sa.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/70 border border-teal-100/50">
                  <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
                    <i className={`${sa.icon} ${sa.color} text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-foreground-950">{sa.label}</p>
                    <p className="text-[9px] text-foreground-500">{sa.description}</p>
                  </div>
                  <span className="text-sm font-bold text-teal-600 shrink-0">+{sa.points}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Cross Links */}
        <div className="mt-10 pt-8 border-t border-background-200/70">
          <p className="text-xs font-semibold text-foreground-400 mb-4 text-center">EXPLORÁ TAMBIÉN</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { to: '/favoritos', label: 'Favoritos', icon: 'ri-heart-3-line' },
              { to: '/historial', label: 'Historial', icon: 'ri-history-line' },
              { to: '/rankings', label: 'Rankings', icon: 'ri-bar-chart-line' },
              { to: '/insignias', label: 'Insignias', icon: 'ri-award-line' },
            ].map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 hover:text-foreground-950 hover:bg-background-200 text-xs font-medium transition-all duration-200 whitespace-nowrap"
              >
                <i className={`${link.icon} text-[10px]`}></i>
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Regla Final */}
        <div className="mt-8 p-6 rounded-2xl bg-teal-100/70 border border-teal-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-teal-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-bookmark-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Guardar debe ser más fácil que buscar después.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            Un solo toque y el contenido queda guardado en tu perfil. Sin menús, sin confirmaciones. Cuando necesités volver, tus filtros de Recientes, Más Vistos, Favoritos y Pendientes te llevan directo a lo que buscás. Así medimos el interés real de la comunidad y premiamos a quienes construyen su biblioteca personal de contenido RFW.
          </p>
        </div>
      </div>
    </div>
  );
}